import { Facility } from "encamp-prisma/generated/browser";

type PartialNullable<T> = { [P in keyof T]?: T[P] | undefined | null };

/** Format a date for use in NNR reason text (e.g. "Closed as of 12/10/2024"). */
function formatReasonDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

export type FormattedNotReportingReasonOptions = {
  reportingYear?: number;
};

/**
 * Returns the full sentence reason for newly-not-reporting emails (e.g. "Closed as of 12/10/2024"
 * or "Closed for the entire reporting year"). Matches the copy in the NNR cover letter reasons block.
 * Use when building TemplateFacility.newlyNotReportingReason.
 */
export function getFormattedOakNotReportingReason(
  facility?: PartialNullable<Facility>,
  options?: FormattedNotReportingReasonOptions
): string | null {
  if (!facility) return null;

  const reportingYear = options?.reportingYear;
  const fallbackSuffix = reportingYear != null ? " for the entire reporting year" : "";

  if (facility.isClosed) {
    if (facility.dateClosed) {
      return `Closed as of ${formatReasonDate(facility.dateClosed)}`;
    }
    return `Closed${fallbackSuffix}`.trim() || "Closed";
  }

  if (facility.colocationReportingFacilityId) {
    return "Co-located with another reporting facility on the same site";
  }

  if (facility.isNotReporting) {
    return "Chemical inventory was below reporting thresholds for the entire reporting year";
  }

  if (facility.isInactive) {
    if (facility.dateInactive) {
      return `Inactive as of ${formatReasonDate(facility.dateInactive)}`;
    }
    return `Inactive${fallbackSuffix}`.trim() || "Inactive";
  }

  if (facility.isNotReportingThroughEncamp) {
    return "No longer reporting through Encamp";
  }

  return null;
}

export function getOakNotReportingReason(facility?: PartialNullable<Facility>) {
  if (!facility) return null;

  if (facility.isClosed) {
    return "Closed";
  }

  if (facility.colocationReportingFacilityId) {
    return "Co-located";
  }

  if (facility.isNotReporting) {
    return "Under Thresholds";
  }

  if (facility.isInactive) {
    return "Is Inactive";
  }

  if (facility.isNotReportingThroughEncamp) {
    return "Without Encamp";
  }

  return null;
}
